import { type SubmitHandler, useForm } from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import { SITE_SECTION_TITLE_MAX_LENGTH, SITE_SECTION_TITLE_MIN_LENGTH } from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Description,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    Fieldset,
    Form,
    Label,
    Submit,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';

import { apiClient } from '../../../state';
import { DraftBadge } from './DraftBadge';
import { SiteStructureItemList } from './SiteStructureItemList';
import { TitleAndIconField } from './TitleAndIconField';

type SectionFormData = {
    title: string;
    icon: string | null;
};

export interface CreateSiteSectionGroupFormProps {
    organization: api.Organization;
    site: api.Site;
    siteSections: api.SiteSection[];
    parent?: string;
    onCreateSectionGroup?: (siteSectionGroup: api.SiteSectionGroup) => void;
}

export function CreateSiteSectionGroupForm(props: CreateSiteSectionGroupFormProps) {
    const { organization, site, siteSections, parent, onCreateSectionGroup } = props;
    const shouldCreateDraftGroup = shouldCreateDraftSectionGroup(siteSections);

    const form = useForm<SectionFormData>({
        defaultValues: {
            title: '',
            icon: null,
        },
    });
    const [isPending, startDialogAction] = useDialogAction();
    const onSubmit: SubmitHandler<SectionFormData> = async (data) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                const { data: siteSectionGroup } =
                    await apiClient.siteSectionGroups.addSectionGroupToSite(
                        organization.id,
                        site.id,
                        {
                            draft: shouldCreateDraftGroup,
                            title: data.title,
                            icon: data.icon,
                            sections: siteSections.map((section) => section.id),
                            parent,
                        }
                    );
                onCreateSectionGroup?.(siteSectionGroup);
            }, 'Error while adding site section group');
            close();
        });
    };
    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody className="space-y-6">
                <Fieldset>
                    <TitleAndIconField
                        form={form}
                        titleName="title"
                        iconName="icon"
                        minLength={SITE_SECTION_TITLE_MIN_LENGTH}
                        maxLength={SITE_SECTION_TITLE_MAX_LENGTH}
                        disabled={isPending}
                        autoFocus
                        titleSuffix={shouldCreateDraftGroup ? <DraftBadge /> : null}
                        titleInputFooter={
                            shouldCreateDraftGroup ? (
                                <div className="text-ui-small text-muted">
                                    <Icon.InfoCircle className="-mt-0.5 mr-1.5 inline-block shrink-0" />
                                    Groups start as drafts and aren't visible on your published site
                                    until undrafted.
                                </div>
                            ) : null
                        }
                    >
                        <Label>Title & Icon</Label>
                        <Description>
                            Choose a title and icon that represents this group.
                        </Description>
                    </TitleAndIconField>
                    {siteSections.length > 0 ? (
                        <SiteStructureItemList
                            description="The following sections will be added to this group:"
                            items={siteSections}
                        />
                    ) : null}
                </Fieldset>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Add" />
            </DialogFooter>
        </Form>
    );
}

/**
 * Return whether a new section group should be created as draft.
 */
export function shouldCreateDraftSectionGroup(siteSections: api.SiteSection[]) {
    // When moving sections to a new group, if at least one selected section is live then the group is created as live.
    return siteSections.length === 0 || siteSections.every((section) => section.draft);
}
